<script setup lang="ts">
import { NConfigProvider, NMessageProvider } from "naive-ui"
import { RouterLink, useRoute, useRouter } from "vue-router"
import { NLayoutHeader, NMenu, NLayoutContent, NDropdown, NAvatar, NSelect, NTag } from 'naive-ui'
import { h, computed } from 'vue'
import { logout } from '@/api/auth'
import { useFarms } from '@/composables/useFarms'

const route  = useRoute()
const router = useRouter()

// Reads from the singleton — fetching is done inside GenericMapView (auth-gated)
const { farms, activeFarm, selectFarm } = useFarms()

const isMapRoute = computed(() => route.name === 'Map')
const isLoginRoute = computed(() => route.name === 'Login')

// ── Farm selector ─────────────────────────────────────────────────────────
const farmOptions = computed(() =>
  farms.value.map(f => ({ label: f.name, value: f.id }))
)

const selectedFarmId = computed({
  get: () => activeFarm.value?.id ?? null,
  set: (id: number | null) => { if (id !== null) selectFarm(id) },
})

// ── Nav menu ──────────────────────────────────────────────────────────────
const activeKey = computed(() => route.name ? String(route.name) : null)

const menuOptions = [
  {
    label: () => h(RouterLink, { to: { name: 'Map' } }, { default: () => 'Map' }),
    key: 'Map'
  },
]

// ── User dropdown ─────────────────────────────────────────────────────────
const userDropdownOptions = [
  { type: 'divider', key: 'd1' },
  { label: 'Logout', key: 'logout' },
]

const handleSelect = async (key: string) => {
  if (key === 'logout') {
    logout()
    router.push({ name: 'Login' })
  }
}

// ── Layout content style (map needs no padding/overflow) ──────────────────
const contentStyle = computed(() => {
  if (isLoginRoute.value) return 'padding: 0; height: 100vh; overflow: hidden;'
  if (isMapRoute.value) return 'padding: 0; height: calc(100vh - 64px); overflow: hidden;'
  return 'padding: 2rem'
})
</script>

<template>
  <n-config-provider>
    <n-message-provider>
      <n-layout-header v-if="!isLoginRoute" bordered style="padding: 0 24px; height: 64px; display: flex; align-items: center; gap: 16px;">
        <a href="https://biokutatas.hu" target="_blank">
          <div class="brand">
            <img src="/omki_logo_positive.svg" width="100" alt="OMKI" />
          </div>
        </a>

        <n-menu
          mode="horizontal"
          :options="menuOptions"
          :value="activeKey"
          responsive
          accordion
        />

        <!-- Farm selector: only visible on the map page -->
        <template v-if="isMapRoute && farms.length > 0">
          <div class="farm-divider" />
          <!-- Single farm: show name as a tag -->
          <n-tag v-if="farms.length === 1" type="success" size="small" round>
            {{ activeFarm?.name ?? farms[0]?.name }}
          </n-tag>
          <!-- Multiple farms: dropdown selector -->
          <n-select
            v-else
            v-model:value="selectedFarmId"
            :options="farmOptions"
            size="small"
            style="width: 200px"
            placeholder="Select farm…"
          />
        </template>

        <div style="flex: 1" />

        <n-dropdown trigger="click" :options="userDropdownOptions" @select="handleSelect">
          <n-avatar
            round
            size="medium"
            src="https://www.gravatar.com/avatar/0000?d=mp"
            style="cursor: pointer"
            fallback-src="https://www.gravatar.com/avatar/0000?d=mp"
          />
        </n-dropdown>
      </n-layout-header>

      <n-layout-content :content-style="contentStyle">
        <router-view v-slot="{ Component }">
          <transition name="fade" mode="out-in">
            <component :is="Component" />
          </transition>
        </router-view>
      </n-layout-content>
    </n-message-provider>
  </n-config-provider>
</template>

<style scoped>
.brand {
  display: flex;
  align-items: center;
  margin-right: 8px;
}

.farm-divider {
  width: 1px;
  height: 24px;
  background: #e0e0e6;
}

.fade-enter-active, .fade-leave-active {
  transition: opacity 0.2s ease;
}
.fade-enter-from, .fade-leave-to {
  opacity: 0;
}

/* Active menu item underline */
:deep(.n-menu-item-content--selected) {
  position: relative !important;
}
:deep(.n-menu-item-content--selected::after) {
  content: "";
  position: absolute;
  bottom: 0;
  left: 15%;
  right: 15%;
  height: 3px;
  background-color: #18a058;
  border-radius: 3px 3px 0 0;
  transition: all 0.3s ease;
}
:deep(.n-menu.n-menu--horizontal .n-menu-item-content.n-menu-item-content--selected) {
  background-color: rgba(24, 160, 88, 0.1) !important;
  border-radius: 6px;
  margin: 4px 0;
  padding: 0 12px;
}
:deep(.n-menu.n-menu--horizontal .n-menu-item-content:hover) {
  background-color: rgba(24, 160, 88, 0.05);
  border-radius: 6px;
}
</style>
